import { useTranslation } from 'react-i18next';
import type { ISiteFloor } from '@interfaces/IScheduler';
import { FloorZoneGrid } from '../FloorZoneGrid/FloorZoneGrid';
import { useZoneSweep } from '../FloorZoneGrid/useZoneSweep';
import styles from './AreaGridPicker.module.css';

// ---------------------------------------------------------------------------
// AreaGridPicker — the compact building map: one row per floor, one cell per
// zone, a floor button that takes (or drops) the whole floor. Used by the
// commit review (editing its draft) and by the drawer's promise block (editing
// the plan through a wrapper), so the same question is asked with the same
// control (2026-09-18 commit UX design, decision 3).
//
// Bulk picking is a PRESS-DRAG across cells: the press captures the anchor's
// intent — pressing an unselected zone ADDS the swept range, pressing a
// selected one REMOVES it — and the sweep is shown live before it commits on
// release, as one change. It spans floors, since the range walks the flattened
// grid order. The layout and the gesture are `FloorZoneGrid` + `useZoneSweep`,
// the same ones the progress board paints with. A plain tap still toggles one
// zone.
// ---------------------------------------------------------------------------

interface IAreaGridPickerProps {
  floors: ISiteFloor[];
  /** Selected zone ids. */
  value: readonly string[];
  onChange: (next: string[]) => void;
  /**
   * Show the map, take no answer. Used when the field has already been seen
   * working here, so WHERE is a record rather than a question (2026-09-20
   * amendment). Disabled buttons receive no pointer events, so the sweep cannot
   * start either — nothing else has to know about this.
   */
  readOnly?: boolean;
}

/** What a sweep is doing, captured from the zone the press started on. */
type SweepIntent = 'add' | 'remove';

const CLASSES = { root: styles.grid, row: styles.floor, floorBtn: styles.floorBtn, floorCount: styles.floorCount };

export function AreaGridPicker({ floors, value, onChange, readOnly = false }: IAreaGridPickerProps) {
  const { t } = useTranslation();
  const selected = new Set(value);

  const apply = (ids: readonly string[], intent: SweepIntent) => {
    const next = new Set(selected);
    for (const id of ids) {
      if (intent === 'add') next.add(id);
      else next.delete(id);
    }
    onChange([...next]);
  };
  const sweep = useZoneSweep<SweepIntent>(floors, apply);
  const intentFrom = (id: string): SweepIntent => (selected.has(id) ? 'remove' : 'add');

  /** What a cell SHOWS: mid-sweep the swept range previews the intent. */
  const shown = (id: string): boolean => {
    const swept = sweep.sweptMeta(id);
    return swept === undefined ? selected.has(id) : swept === 'add';
  };

  if (floors.length === 0) {
    return <p className={styles.empty}>{t('scheduler.panel.locations.noCatalog')}</p>;
  }

  return (
    <FloorZoneGrid
      rows={floors}
      sweep={sweep}
      classes={CLASSES}
      ariaLabel={t('scheduler.commit.row.areas')}
      disabled={readOnly}
      floor={(floor) => {
        const ids = floor.zones.map((zone) => zone.id);
        const taken = ids.filter(shown).length;
        return {
          // The floor button takes the whole floor — or drops it, once every
          // zone on it is taken.
          onClick: () => apply(ids, ids.every((id) => selected.has(id)) ? 'remove' : 'add'),
          count: taken > 0 ? taken : null,
          ariaPressed: taken === ids.length && ids.length > 0,
          hint: t('scheduler.panel.locations.selectAllOnFloor', { floor: floor.name }),
        };
      }}
      cell={(zone, floor) => {
        const on = shown(zone.id);
        return {
          // "Wet paint": previewed in its next state until the release commits it.
          className: on === selected.has(zone.id) ? styles.zone : `${styles.zone} ${styles.wet}`,
          ariaLabel: t('scheduler.panel.locations.cellLabel', { floor: floor.name, zone: zone.name }),
          ariaPressed: on,
          pressMeta: intentFrom(zone.id),
          onTap: () => apply([zone.id], intentFrom(zone.id)),
          content: zone.name,
        };
      }}
    />
  );
}
